import { useEffect, useRef } from 'react';
import {
  GroupedVirtuosoHandle,
  ListRange,
  ScrollSeekConfiguration,
  StateSnapshot,
} from 'react-virtuoso';
import useDebounceFunction from '../../../../../components/Hooks/useDebouncedFunction';
import { useObjectContext } from '../../../../../components/v2/layout/ObjectScreen/hooks/useObjectContext';
import { useBatchUpdateCatalogSections } from '../../../../../services/modules/catalogs/catalogs.mutations';
import useOrderBookStore from '../../../../../stores/OrderBook/orderBook.store';
import useCatalogSectionsController from '../../hooks/CatalogSectionsController/useCatalogSectionsController';
import { CatalogItemsLimits } from '../../product-catalog-es.types';
import {
  RenameCatalogSectionParams,
  UseCatalogProductsListV2ViewParams,
} from '../catalogProductsListV2.types';
import { useCatalogSections } from '../contexts/CatalogSections.context';
import { useCatalogView } from '../contexts/CatalogView.context';
import { ResolvedRow } from '../utils/catalogDragAndDrop.types';
import { USER_SCROLL_EVENTS } from '../utils/catalogSectionScroll.utils';
import { CatalogViewMode } from '../view/catalogView.types';
import { useCatalogDragEnabled } from './useCatalogDragEnabled';
import { useCatalogListScrollReset } from './useCatalogListScrollReset';
import { useCatalogProductsDragAndDrop } from './useCatalogProductsDragAndDrop';
import { useCatalogProductsListV2 } from './useCatalogProductsListV2';
import { useCatalogSectionScrollSpy } from './useCatalogSectionScrollSpy';
import { useCatalogSnapshotDetails } from './useCatalogSnapshotDetails';

const EMPTY_SECTION_LIMITS: CatalogItemsLimits[] = [];

const EMPTY_RESOLVED_ROWS: ResolvedRow[] = [];

const SCROLL_SEEK_ENTER_PX_PER_100MS = 1000;

const SCROLL_SEEK_EXIT_PX_PER_100MS = 250;

interface RestoredSnapshot {
  mode: CatalogViewMode;
  value: StateSnapshot | undefined;
}

const isScrollingToTarget = (targetIndex: number | null, startIndex: number): boolean => {
  if (targetIndex === null) {
    return false;
  }

  const delta = startIndex - targetIndex;
  return delta < -1 || delta > 1;
};

const useCatalogProductsListV2View = ({
  catalogId,
  maxItemsPerRow,
  pageSize,
  isOfflineMode,
  canPersist,
}: UseCatalogProductsListV2ViewParams) => {
  const { scrollParent, stickyToolbar, stickyFilterBar } = useObjectContext();
  const { effectiveMode } = useCatalogView();
  const { setVisibleSectionId, releasePinnedSection, registerScroller } = useCatalogSections();

  const virtuoso = useRef<GroupedVirtuosoHandle>(null);
  const lastRange = useRef<ListRange | null>(null);

  const { handleDebounceFunction: debounceRangeChange } = useDebounceFunction({ debounceTime: 10 });

  const list = useCatalogProductsListV2({
    catalogId,
    maxItemsPerRow,
    pageSize,
    isOfflineMode,
    canPersist,
  });

  const { mutate: applySections } = useBatchUpdateCatalogSections({ catalogId });

  const renameSection = ({ sectionId, name }: RenameCatalogSectionParams) => {
    if (list.status !== 'ready') {
      return;
    }

    applySections({
      sections: list.sections.map((section) => ({
        id: section.catalogSectionId,
        name: section.catalogSectionId === sectionId ? name : section.catalogSectionName,
      })),
    });
  };

  const snapshotDetails = useCatalogSnapshotDetails({ catalogId, isOfflineMode });
  const activeOrderId = useOrderBookStore((state) => state.selectedOrder?.id);

  const isDragEnabled = useCatalogDragEnabled({ catalogId, isOfflineMode, canPersist });
  const resolvedRows = list.status === 'ready' ? list.resolvedRows : EMPTY_RESOLVED_ROWS;

  const dragAndDrop = useCatalogProductsDragAndDrop({
    catalogId,
    canPersist,
    isEnabled: isDragEnabled,
    rows: resolvedRows,
    scrollParent,
  });

  const sectionLimits = list.status === 'ready' ? list.sectionLimits : EMPTY_SECTION_LIMITS;
  const controller = useCatalogSectionsController({
    sectionLimits,
    isOfflineMode,
    scrollParent,
    stickyToolbar,
    stickyFilterBar,
    mode: effectiveMode,
    virtuoso,
  });

  const visibleSectionId = useCatalogSectionScrollSpy({
    scrollParent,
    stickyToolbar,
    stickyFilterBar,
    sectionLimits,
  });

  const restoredSnapshot = useRef<RestoredSnapshot | null>(null);

  if (restoredSnapshot.current?.mode !== effectiveMode) {
    restoredSnapshot.current = { mode: effectiveMode, value: controller.getSnapshot() };
  }

  const restoreStateFrom = restoredSnapshot.current.value;

  useCatalogListScrollReset({ scrollParent, hasSnapshot: !!controller.getSnapshot() });

  const processRange = (range: ListRange) => {
    controller.reachTargetIndex();

    if (list.status === 'ready') {
      list.onRangeChanged(range);
    }
  };

  const applyRangeChange = (range: ListRange) =>
    debounceRangeChange(() => {
      if (isScrollingToTarget(controller.targetIndex, range.startIndex)) {
        return;
      }

      processRange(range);
    });

  const handleRangeChanged = (range: ListRange) => {
    lastRange.current = range;

    if (controller.isScrollingFast) {
      return;
    }

    applyRangeChange(range);
  };

  const persistSnapshot = () => {
    virtuoso.current?.getState((snapshot) =>
      controller.updateSnapshot({
        snapshot,
        name: snapshotDetails.name,
        search: snapshotDetails.search,
      }),
    );
  };

  const releaseInterruptedScrollTarget = () => {
    if (controller.targetIndex !== null && lastRange.current) {
      processRange(lastRange.current);
    }
  };

  const handleIsScrolling = (isScrolling: boolean) => {
    if (isScrolling) {
      return;
    }

    persistSnapshot();
    releaseInterruptedScrollTarget();
  };

  const scrollSeekConfiguration: ScrollSeekConfiguration = {
    enter: (velocity) => Math.abs(velocity) > SCROLL_SEEK_ENTER_PX_PER_100MS,
    change: controller.startScrollingFast,
    exit: (velocity, range) => {
      const shouldExit = Math.abs(velocity) < SCROLL_SEEK_EXIT_PX_PER_100MS;
      if (shouldExit) {
        controller.endScrollingFast();
        applyRangeChange(range);
      }
      return shouldExit;
    },
  };

  useEffect(() => {
    registerScroller(controller.scrollToSection);

    return () => registerScroller(null);
  });

  useEffect(() => {
    if (visibleSectionId) {
      setVisibleSectionId(visibleSectionId);
    }
  }, [visibleSectionId, setVisibleSectionId]);

  useEffect(() => {
    if (!scrollParent) {
      return;
    }

    for (const event of USER_SCROLL_EVENTS) {
      scrollParent.addEventListener(event, releasePinnedSection, { passive: true });
    }

    return () => {
      for (const event of USER_SCROLL_EVENTS) {
        scrollParent.removeEventListener(event, releasePinnedSection);
      }
    };
  }, [scrollParent, releasePinnedSection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the controller reads the storage target on call; the catalog it writes, the metadata and the active order are the triggers
  useEffect(() => {
    controller.updateSnapshotDetails({
      name: snapshotDetails.name,
      search: snapshotDetails.search,
    });
  }, [catalogId, snapshotDetails.name, snapshotDetails.search, activeOrderId]);

  return {
    list,
    virtuoso,
    renameSection,
    scrollParent,
    dragAndDrop,
    restoreStateFrom,
    handleRangeChanged,
    handleIsScrolling,
    scrollSeekConfiguration,
  };
};

export default useCatalogProductsListV2View;
